import { NgxSpinnerService } from 'ngx-spinner';
import {
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavbarService } from './core/components/navbar/navbar.service';
import {
  ThemeList,
  ThemeService,
} from './shared/service/theme.service';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import {
  GetUserInfo,
  Login,
  Logout,
} from './store/auth/auth.actions';
import { Router, NavigationEnd } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
} from 'rxjs';
import { AuthStateModel } from './store/auth/auth.model';
import { TranslateService } from '@ngx-translate/core';
import { SiteConfigService } from './shared/service/site-config-service';
import {
  featureFlag,
  environment,
} from '../environments/environment';
import { finalize, takeUntil, tap, filter } from 'rxjs/operators';
import { setTheme } from 'ngx-bootstrap/utils';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { TokenService } from './core/authentication/token.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Title } from '@angular/platform-browser';
import Survicate from '@survicate/survicate-web-package/survicate_widget';
import {
  WebSocketService,
  WebSocketMessage,
} from './core/services/websocket.service';
import { DuplicateLoginDialogComponent } from '@core/components/duplicate-login-dialog/duplicate-login-dialog.component';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { HttpErrorResponse } from '@angular/common/http';
import { TabTrackerService } from '@shared/service/tab-tracker.service';
import { Location } from '@angular/common';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  navbarActive = false;
  defaultLang = 'th';
  loadingSuccess$ = new BehaviorSubject<boolean>(false);
  countdown?: number;
  sessionTimeOut?: number;
  private subscription = new Subscription();
  private destroy$ = new Subject<void>();

  @ViewChild('sessionTimeoutModal', { static: true })
  sessionTimeoutModal: ElementRef;
  @ViewChild('notificationLogin', { static: true })
  notificationLogin: ElementRef;
  modal: NgbModalRef;

  // @Select(AuthState.token)
  userList$: Observable<AuthStateModel>;

  passwordPolicyDetail = {
    minutes_before_session_timeout: 30,
  };

  constructor(
    private authService: AuthenticationService,
    private router: Router,
    private spinnerService: NgxSpinnerService,
    private navbar: NavbarService,
    private themeService: ThemeService,
    private siteConfigService: SiteConfigService,
    private actions$: Actions,
    private store: Store,
    private translateService: TranslateService,
    private idle: Idle,
    private keepalive: Keepalive,
    private tokenService: TokenService,
    private modalService: NgbModal,
    private titleService: Title,
    private zone: NgZone,
    private websocketService: WebSocketService,
    private tabTrackerService: TabTrackerService,
    private location: Location,
  ) {
    this.userList$ = this.store.select((state) => {
      return state.AuthState.token;
    });
    setTheme('bs4');
    this.actions$.pipe(ofActionDispatched(Logout)).subscribe(() => {
      const currentPath = this.location.path(false);
      if (currentPath === '/login') {
        this.router.navigate(['/login']);
        return;
      }

      if (currentPath.startsWith('/s11-documents/')) {
        const queryParams = { returnUrl: currentPath };
        this.router.navigate(['/login'], { queryParams });
      } else {
        this.router.navigate(['/login']);
      }
    });

    this.initializeLanguage();
  }

  reset(): void {
    // we'll call this method when we want to start/reset the idle process
    // reset any component state and be sure to call idle.watch()
    this.idle.watch();
    this.countdown = undefined;
    this.keepalive.start();
  }

  ngOnInit(): void {
    // make sure that site setting is loaded before showing website.
    this.checkSiteConfig().then(() => {
      this.getTheme().subscribe(() => {
        this.navbar.data.subscribe((res) => {
          this.navbarActive = res;
        });
      });
    });

    this.loadSurvicateScript();
    if (
      !this.store.selectSnapshot<string>(
        (state) => state.auth.access,
      ) &&
      this.navbarActive
    ) {
      this.store.dispatch(GetUserInfo);
    }
    // right when the component initializes, start reset state and start watching
    this.setIdle().then(() => {
      this.reset();
      this.refreshToken();
    });
    this.subscribeDuplicateLoginMessages();
    this.subscribeLoginAction();
    this.subscribeLoginRouteReconnect();
    this.detectTabsOpen();
  }

  private subscribeDuplicateLoginMessages(): void {
    this.subscription.add(
      this.websocketService.messagesSubject
        .pipe(
          takeUntil(this.destroy$),
          filter(
            (m): m is WebSocketMessage =>
              !!m &&
              m.type === 'duplicate_login_notification' &&
              !!m.username,
          ),
          filter(() => !this.router.url.includes('login')),
          filter(
            (m) =>
              m.username ===
              this.authService.currentUserInfo?.user.username,
          ),
        )
        .subscribe((m) =>
          this.showDuplicateLoginConfirmationDialog(
            m.username as string,
            m.session_id as string,
          ),
        ),
    );
  }

  private subscribeLoginAction(): void {
    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(Login), takeUntil(this.destroy$))
        .subscribe(() => {
          this.keepalive.start();
        }),
    );
  }

  private subscribeLoginRouteReconnect(): void {
    this.subscription.add(
      this.router.events
        .pipe(
          takeUntil(this.destroy$),
          filter(
            (e): e is NavigationEnd => e instanceof NavigationEnd,
          ),
          filter(() => {
            const s = this.websocketService.socket;
            return (
              !s ||
              (s.readyState !== WebSocket.OPEN &&
                s.readyState !== WebSocket.CONNECTING)
            );
          }),
          tap(() => {
            this.websocketService.connect();
          }),
        )
        .subscribe(),
    );
  }

  detectTabsOpen() {
    if (
      this.tabTrackerService.shouldLogoutFromPreviousSession() &&
      this.authService?.currentUserValue
    ) {
      this.store.dispatch(new Logout(true));
    }
  }

  showDuplicateLoginConfirmationDialog(
    username: string,
    sessionId: string,
  ): void {
    this.modalService.dismissAll();
    const modalRef = this.modalService.open(
      DuplicateLoginDialogComponent,
      {
        centered: true,
        backdrop: 'static',
        keyboard: false,
      },
    );
    modalRef.componentInstance.data = {
      username: username,
      sessionIdWs: sessionId,
      timestamp: new Date().toISOString(),
    };
  }

  loadSurvicateScript(): void {
    if (!environment.production) {
      return;
    }
    if (!navigator.onLine) {
      return;
    }
    const workSpaceKey = '434e22a79e4844789f7522c5cf157bc4';
    const globalWin = window as Window & {
      __survicateInitialized?: boolean;
    };
    if (globalWin.__survicateInitialized) {
      return;
    }
    try {
      Survicate.init({ workspaceKey: workSpaceKey });
      globalWin.__survicateInitialized = true;
    } catch (e) {
      return;
    }
  }

  getSessionPolicy(): Promise<void> {
    return new Promise((resolve) => {
      this.siteConfigService.getSessionPolicy().subscribe({
        next: (config: any) => {
          this.sessionTimeOut = config?.session_timeout;
          resolve();
        },
      });
    });
  }

  async setIdle() {
    await this.getSessionPolicy();

    if (!this.sessionTimeOut && featureFlag.ndid) {
      this.sessionTimeOut = 60;
    }
    this.idle.setIdle(60 * (this.sessionTimeOut || 60));
    this.idle.setTimeout(15);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.onIdleStart.subscribe(() => {
      if (!this.sessionTimeOut) {
        this.reset();
        return;
      }
      if (this.router.url.includes('login')) {
        this.reset();
        return;
      }

      this.modal?.dismiss('');
      this.modal = this.modalService.open(this.sessionTimeoutModal, {
        centered: true,
        backdrop: 'static',
        keyboard: false,
      });
    });
    this.idle.onTimeout.subscribe(() => {
      this.logout();
    });
    this.idle.onTimeoutWarning.subscribe((seconds) => {
      this.countdown = seconds;
    });
    this.keepalive.interval(4 * 60);
    this.keepalive.onPing.subscribe(() => this.refreshToken());
  }

  /**
   * Set the initial language in localStorage
   * that will be used to stuff in HTTP Headers - `Accept-Language`
   * and for translation.
   */
  initializeLanguage(): void {
    let lang = localStorage.getItem('lang');
    if (!lang || lang === 'null') {
      localStorage.setItem('lang', 'th');
      lang = 'th';
    }
    this.defaultLang = lang;
    this.translateService.setDefaultLang(this.defaultLang);
    this.translateService.use(this.defaultLang);
  }

  getTheme(): Observable<ThemeList> {
    this.loadingSuccess$.next(false);
    this.spinnerService.show();
    return this.themeService.getActiveTheme().pipe(
      tap((theme) => {
        this.themeService.setThemeList(theme);
      }),
      finalize(() => {
        this.loadingSuccess$.next(true);
        this.spinnerService.hide();
      }),
    );
  }

  checkSiteConfig(): Promise<void> {
    return new Promise((resolve) => {
      this.titleService.setTitle(featureFlag.page_title);

      if (
        featureFlag.maintenance_mode === true &&
        !this.router.url.startsWith('/website-maintenance')
      ) {
        // force redirect to maintenance page when website is in maintenance mode.
        this.zone.run(() => {
          this.router.navigate(['/website-maintenance']).then(() => {
            resolve();
          });
        });
      } else if (
        featureFlag.maintenance_mode !== true &&
        this.router.url.startsWith('/website-maintenance')
      ) {
        // force redirect to login mode when website is not in maintenance.
        this.zone.run(() => {
          this.router.navigate(['/login']).then(() => {
            resolve();
          });
        });
      } else {
        resolve();
      }
    });
  }

  refreshToken(): void {
    if (this.router.url.includes('login')) {
      return;
    }

    const token = this.tokenService.getRefreshToken();
    if (token) {
      this.tokenService.refreshToken(token).subscribe({
        next: (tk: any) => {
          this.tokenService.saveToken(tk.access);
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 401) {
            this.logout();
          }
        },
      });
    }
  }

  logout(isCloseTab = false): void {
    this.dismissAll();
    this.reset();
    this.store.dispatch(new Logout(isCloseTab));
  }

  dismissAll(): void {
    this.modalService.dismissAll();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
